<template>
  <v-form
    ref="form"
    v-model="valid"
    lazy-validation
    style="width: 100%; max-width: 890px"
    @submit.prevent="validateForm"
  >
    <div class="pa-8 fill-height" style="width: 100%">
      <v-row>
        <v-col>
          <template v-for="item in allItems">
            <SmsMessageTripCard
              :item="item"
              :key="item.uuid"
              :type="item._type"
              :item-uuid="item.uuid"
              :ref-form="$refs.form"
              :validating-form="validatingForm"
            />
          </template>
        </v-col>
      </v-row>
      <transition name="slide-y-transition" mode="out-in">
        <v-row v-if="diffItemsCount">
          <v-col class="d-flex justify-end">
            <v-btn
              type="submit"
              rounded
              color="primary"
              :disabled="disabledSendButton"
              :loading="sendingMessage"
            >
              <v-icon small class="pr-4">mdi-send</v-icon>
              Skicka
            </v-btn>
          </v-col>
        </v-row>
      </transition>
    </div>
  </v-form>
</template>

<script>
import SmsMessageTripCard from './SmsMessageTripCard'
import { format } from 'date-fns'
import orderBy from 'lodash.orderby'
import { get } from 'vuex-pathify'
import diffFactory from '../../../helpers/diffFactory'
import gql from 'graphql-tag'

export default {
  name: 'SmsMessageForm',

  components: {
    SmsMessageTripCard
  },

  filters: {
    dateTime(date) {
      return format(new Date(date), 'yyyy-MM-dd HH:mm')
    }
  },

  data: () => ({
    validatingForm: false,
    valid: true,
    sendingMessage: false
  }),

  computed: {
    trainDeviations: get('trainDeviation/data'),
    diffItemsCount: get('smsMessage/diffItemsCount'),
    diffTrainDeviations: get('smsMessage/diffTrainDeviations'),
    diffTrainDeviationsCancelSections: get(
      'smsMessage/diffTrainDeviationsCancelSections'
    ),
    selectedEventUUID: get('event/selectedUUID'),
    form: get('smsMessage/form'),
    model: get('smsMessage/model'),

    list() {
      return diffFactory(
        this.model?.trips?.map(x => x?.trip_uuid)?.filter(Boolean) || [],
        x => x,
        this.trainDeviations
      ).filter(x =>
        ['PARTIAL_CANCELLATION', 'CANCELED'].includes(x.text.deviation_type)
      )
    },

    currentItems() {
      return this.list
        .filter(x => x.current)
        .map(x => ({
          ...x,
          _type: 'current'
        }))
    },

    newItems() {
      return this.list
        .filter(x => x.new)
        .map(x => ({
          ...x,
          _type: 'new'
        }))
    },

    missingItems() {
      return this.list
        .filter(x => x.missing)
        .map(x => ({
          ...x,
          _type: 'missing'
        }))
    },

    allItems() {
      return orderBy(
        [...this.currentItems, ...this.missingItems, ...this.newItems],
        [x => x.text.departure],
        ['asc']
      )
    },

    disabledSendButton() {
      const items = [
        ...this.newItems,
        ...this.currentItems.filter(x => this.isDiff(x.uuid))
      ]

      return (
        !this.missingItems.length &&
        !items.map(x => this.form[x.uuid]).some(x => x._checked)
      )
    }
  },
  methods: {
    isDiff(uuid) {
      const isOldDiff =
        ['CANCELED_STOP'].includes(this.trainDeviations[uuid].deviation_type) ||
        !this.form[uuid]?.data?.cancel_sections.length
      if (isOldDiff) {
        return Boolean(
          this.diffTrainDeviations?.find(x => x?.trip_uuid === uuid)
        )
      }

      return this.diffTrainDeviationsCancelSections?.find(
        x => x?.trip_uuid === uuid
      )
    },

    validateForm() {
      this.validatingForm = true

      this.$nextTick(this.confirmSend)
    },

    async confirmSend() {
      const isValidForm = await this.$refs.form.validate()

      if (!isValidForm) {
        this.validatingForm = false
        return
      }

      this.sendingMessage = true

      const mapCurrentUUIDs = array =>
        array
          .filter(x => x.current)
          .map(x => this.form[x.uuid])
          .map(x => {
            if (!x._checked) {
              return {
                ...x,
                data:
                  this.model?.trips?.find(y => y?.trip_uuid === x?.trip_uuid)
                    ?.data || null
              }
            }
            return x
          })
          .map(x => {
            delete x._checked
            return x
          })

      const mapNewUUIDs = array =>
        array
          .filter(x => x.new)
          .map(x => this.form[x.uuid])
          .filter(x => x._checked)
          .map(x => {
            delete x._checked
            return x
          })

      const setOriginalDataProps = item => {
        if (item.data?.cancel_sections?.length) {
          return {
            ...item,
            data: {
              cancel_sections: item.data.cancel_sections,
              bookings: item.data.bookings,
              reason_code: item.data.reason_code,
              sms_text: item.data.sms_text
            }
          }
        }

        return item
      }

      const payload = {
        event_uuid: this.selectedEventUUID,
        trips: [
          ...mapCurrentUUIDs(this.list).map(setOriginalDataProps),
          ...mapNewUUIDs(this.list).map(setOriginalDataProps)
        ]
      }

      await this.$apollo.mutate({
        mutation: gql`
          mutation SendSmsMessage($payload: SendSmsMessageInput!) {
            sendSmsMessage(payload: $payload) {
              sms_message_uuid
            }
          }
        `,
        variables: {
          payload: payload
        }
      })

      this.$store.set('smsMessage/model', {
        ...this.model,
        ...payload
      })

      this.$store.dispatch('smsMessage/setForm')

      this.$eventBus.$emit('event/refetch')
      this.validatingForm = false
      this.sendingMessage = false
    }
  }
}
</script>
